import { useEffect, useState } from "react"
import { supabase } from "../supabaseClient"
import { getActiveYear } from "../utils/activeYear"

interface ChampionEntry {
  id: string
  entry_id: string
  entry_name: string
  user_name: string
  champion_team_name: string
  champion_team_id: number
  champion_team_seed: number
  champion_team_region: string
  champion_team_alive: boolean
  championship_total_points: number
  championship_total_fouls: number
  in_contest: boolean // Whether they've entered the champion side pot
}

interface ChampionTeamGroup {
  team_name: string
  team_id: number
  team_seed: number
  team_region: string
  team_alive: boolean
  entries: ChampionEntry[]
}

interface ChampionTeam {
  id: number
  name: string
  seed: number
  region: string
  is_eliminated: boolean
}

type ContestFilter = 'all' | 'in' | 'out'

export default function ChampionGame() {
  const [entries, setEntries] = useState<ChampionEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [activeYear, setActiveYearState] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [championEntryFee, setChampionEntryFee] = useState<number>(5)
  
  // Filter state
  const [contestFilter, setContestFilter] = useState<ContestFilter>('in')
  const [aliveOnly, setAliveOnly] = useState(false)
  const [teamFilters, setTeamFilters] = useState<Set<number>>(new Set())
  const [showTeamDropdown, setShowTeamDropdown] = useState(false)
  const [allChampionTeams, setAllChampionTeams] = useState<ChampionTeam[]>([])
  
  // Access control
  const [entriesOpen, setEntriesOpen] = useState<boolean>(true)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    const year = await getActiveYear()
    setActiveYearState(year)

    // Get champion entry fee and entries_open from years table
    const { data: yearData } = await supabase
      .from('years')
      .select('champion_entry_fee, entries_open')
      .eq('year', year)
      .single()
    
    setChampionEntryFee(yearData?.champion_entry_fee || 5)
    setEntriesOpen(yearData?.entries_open ?? true)
    
    // Check if current user is admin
    const { data: { user } } = await supabase.auth.getUser()
    if (user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single()
      setIsAdmin(profile?.is_admin || false)
    }

    // Load ALL entries that have a champion pick
    const { data: entriesData } = await supabase
      .from('entries')
      .select(`
        id,
        entry_name,
        champion_team_id,
        championship_total_points,
        championship_total_fouls,
        user_id
      `)
      .eq('year', year)
      .not('champion_team_id', 'is', null)

    if (!entriesData || entriesData.length === 0) {
      setEntries([])
      setLoading(false)
      return
    }

    // Load champion_entries to see who is in the contest
    const { data: championData } = await supabase
      .from('champion_entries')
      .select('entry_id')
      .eq('year', year)

    const contestEntryIds = new Set((championData || []).map(ce => ce.entry_id))

    // Fetch profiles for display names
    const userIds = Array.from(new Set(entriesData.map(e => e.user_id).filter(Boolean)))
    const { data: profilesData } = await supabase
      .from('profiles')
      .select('id, display_name')
      .in('id', userIds)

    // Fetch tournament teams for champion picks
    const teamIds = Array.from(new Set(entriesData.map(e => e.champion_team_id).filter(Boolean)))
    const { data: teamsData } = await supabase
      .from('tournament_teams')
      .select('id, seed, region, is_eliminated, school:team_schools(name)')
      .in('id', teamIds)

    // Create lookup maps
    const profilesMap = new Map((profilesData || []).map(p => [p.id, p]))
    const teamsMap = new Map((teamsData || []).map((t: any) => [t.id, t]))

    // Build list of champion teams for filter dropdown
    const championTeams: ChampionTeam[] = (teamsData || []).map((t: any) => ({
      id: t.id,
      name: t.school?.name || 'Unknown',
      seed: t.seed || 0,
      region: t.region || '',
      is_eliminated: t.is_eliminated || false
    })).sort((a: ChampionTeam, b: ChampionTeam) => a.seed - b.seed)
    setAllChampionTeams(championTeams)

    // Transform the data
    const transformedEntries: ChampionEntry[] = entriesData.map((entry: any) => {
      const profile = entry.user_id ? profilesMap.get(entry.user_id) : null
      const team = entry.champion_team_id ? teamsMap.get(entry.champion_team_id) : null
      
      return {
        id: entry.id,
        entry_id: entry.id,
        entry_name: entry.entry_name || 'Unknown',
        user_name: profile?.display_name || 'Anonymous',
        champion_team_name: team?.school?.name || 'Unknown',
        champion_team_id: team?.id || 0,
        champion_team_seed: team?.seed || 0,
        champion_team_region: team?.region || '',
        champion_team_alive: !team?.is_eliminated,
        championship_total_points: entry.championship_total_points || 0,
        championship_total_fouls: entry.championship_total_fouls || 0,
        in_contest: contestEntryIds.has(entry.id)
      }
    })

    setEntries(transformedEntries)
    setLoading(false)
  }

  // Filter entries
  const filteredEntries = entries.filter(e => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      if (!(
        e.entry_name.toLowerCase().includes(query) ||
        e.user_name.toLowerCase().includes(query) ||
        e.champion_team_name.toLowerCase().includes(query)
      )) {
        return false
      }
    }
    
    // Contest filter (in side pot or not)
    if (contestFilter === 'in' && !e.in_contest) return false
    if (contestFilter === 'out' && e.in_contest) return false
    
    // Alive filter
    if (aliveOnly && !e.champion_team_alive) return false
    
    // Team filter - only show entries whose champion is in the selected teams
    if (teamFilters.size > 0 && !teamFilters.has(e.champion_team_id)) return false
    
    return true
  })

  // Group entries by champion team
  const groupedEntries: ChampionTeamGroup[] = []
  const teamMap = new Map<number, ChampionEntry[]>()
  
  filteredEntries.forEach(e => {
    const key = e.champion_team_id
    if (!teamMap.has(key)) {
      teamMap.set(key, [])
    }
    teamMap.get(key)!.push(e)
  })
  
  teamMap.forEach((entries, team_id) => {
    groupedEntries.push({
      team_name: entries[0]?.champion_team_name || 'Unknown',
      team_id,
      team_seed: entries[0]?.champion_team_seed || 0,
      team_region: entries[0]?.champion_team_region || '',
      team_alive: entries[0]?.champion_team_alive || false,
      entries: entries.sort((a, b) => a.championship_total_points - b.championship_total_points)
    })
  })
  
  // Sort groups by seed, then by entry count
  groupedEntries.sort((a, b) => {
    if (a.team_seed !== b.team_seed) return a.team_seed - b.team_seed
    return b.entries.length - a.entries.length
  })

  // Stats
  const contestEntries = entries.filter(e => e.in_contest)
  const inContestCount = contestEntries.length
  const totalPot = inContestCount * championEntryFee
  const aliveInContestCount = contestEntries.filter(e => e.champion_team_alive).length
  const eliminatedInContestCount = inContestCount - aliveInContestCount
  
  // Get selected team names for display
  const selectedTeamNames = allChampionTeams.filter(t => teamFilters.has(t.id))

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-gray-500">
        Loading champion contest...
      </div>
    )
  }

  // Block access if entries are closed (admins can always view)
  if (!entriesOpen && !isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        <h2 className="text-xl font-bold text-[#1e3a5f] mb-2">Contest Not Available</h2>
        <p className="text-gray-500 mb-6 max-w-md">
          The champion contest is not available while entries are closed. 
          Check back later!
        </p>
        <a 
          href="/dashboard" 
          className="px-6 py-3 bg-[#1e3a5f] text-white rounded-lg font-medium hover:bg-[#002a6b]"
        >
          Back to Dashboard
        </a>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1e3a5f]">National Champion Contest</h1>
        <p className="text-gray-500">Side pot entries for {activeYear}</p>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <div>
          <span className="font-bold text-[#1e3a5f]">{inContestCount}</span>{' '}
          <span className="text-gray-500">in contest</span>
        </div>
        <span className="text-gray-300">•</span>
        <div>
          <span className="font-bold text-green-600">${totalPot}</span>{' '}
          <span className="text-gray-500">pot</span>
        </div>
        <span className="text-gray-300">•</span>
        <div>
          <span className="font-bold text-green-500">{aliveInContestCount}</span>{' '}
          <span className="text-gray-500">alive</span>
        </div>
        <span className="text-gray-300">•</span>
        <div>
          <span className="font-bold text-red-400">{eliminatedInContestCount}</span>{' '}
          <span className="text-gray-500">out</span>
        </div>
      </div>

      {/* Filters */}
      <div className="space-y-3">
        {/* Row 1: Search + Contest Filter + Alive toggle */}
        <div className="flex flex-wrap gap-2">
          {/* Search */}
          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search entries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-9 pr-3 border border-gray-200 rounded-lg text-sm bg-white"
            />
          </div>
          
          {/* Contest Filter (In/Out of side pot) */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setContestFilter('all')}
              className={`px-3 h-10 text-sm font-medium transition-colors ${
                contestFilter === 'all'
                  ? 'bg-[#1e3a5f] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setContestFilter('in')}
              className={`px-3 h-10 text-sm font-medium transition-colors border-l border-gray-200 ${
                contestFilter === 'in'
                  ? 'bg-[#1e3a5f] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              In Contest
            </button>
            <button
              onClick={() => setContestFilter('out')}
              className={`px-3 h-10 text-sm font-medium transition-colors border-l border-gray-200 ${
                contestFilter === 'out'
                  ? 'bg-[#1e3a5f] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Not In
            </button>
          </div>

          {/* Alive Only toggle */}
          <button
            onClick={() => setAliveOnly(!aliveOnly)}
            className={`h-10 px-3 text-sm font-medium rounded-lg border transition-colors ${
              aliveOnly
                ? 'bg-green-500 text-white border-green-500'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
            }`}
          >
            Alive Only
          </button>
        </div>

        {/* Row 2: Team Filter (full width on mobile) */}
        <div className="relative">
          <button
            onClick={() => setShowTeamDropdown(!showTeamDropdown)}
            className={`h-10 px-3 flex items-center gap-2 text-sm border rounded-lg transition-colors ${
              teamFilters.size > 0 
                ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' 
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
            }`}
          >
            {teamFilters.size > 0 ? `${teamFilters.size} Team${teamFilters.size > 1 ? 's' : ''}` : 'Filter by Team'}
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          
          {showTeamDropdown && (
            <div className="absolute left-0 top-full mt-1 w-72 max-h-80 overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-200 z-20">
              {/* Header with Clear All */}
              <div className="sticky top-0 bg-white border-b border-gray-100 p-2 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500">Filter by champion pick</span>
                {teamFilters.size > 0 && (
                  <button
                    onClick={() => setTeamFilters(new Set())}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Clear All
                  </button>
                )}
              </div>
              {allChampionTeams.map(team => {
                const isSelected = teamFilters.has(team.id)
                const pickCount = entries.filter(e => e.champion_team_id === team.id).length
                return (
                  <button
                    key={team.id}
                    onClick={() => {
                      const newFilters = new Set(teamFilters)
                      if (isSelected) {
                        newFilters.delete(team.id)
                      } else {
                        newFilters.add(team.id)
                      }
                      setTeamFilters(newFilters)
                    }}
                    className={`w-full flex items-center gap-2 p-2 text-left hover:bg-gray-50 text-sm ${
                      isSelected ? 'bg-[#1e3a5f]/5' : ''
                    }`}
                  >
                    {/* Checkbox */}
                    <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                      isSelected 
                        ? 'bg-[#1e3a5f] border-[#1e3a5f]' 
                        : 'border-gray-300'
                    }`}>
                      {isSelected && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </div>
                    {/* Status Dot */}
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      team.is_eliminated ? 'bg-red-400' : 'bg-green-500'
                    }`} />
                    {/* Seed */}
                    <span className={`text-xs font-semibold w-4 ${
                      team.is_eliminated ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {team.seed}
                    </span>
                    {/* Team Name */}
                    <span className={`flex-1 truncate ${
                      team.is_eliminated ? 'text-gray-400 line-through' : 'text-gray-700'
                    }`}>
                      {team.name}
                    </span>
                    {/* Pick Count */}
                    <span className="text-xs text-gray-400">{pickCount}</span>
                  </button>
                )
              })}
              {/* Done button at bottom */}
              <div className="sticky bottom-0 bg-white border-t border-gray-100 p-2">
                <button
                  onClick={() => setShowTeamDropdown(false)}
                  className="w-full py-2 bg-[#1e3a5f] text-white text-sm font-medium rounded-lg hover:bg-[#153050]"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Active Filters */}
        {(searchQuery || contestFilter !== 'in' || aliveOnly || teamFilters.size > 0) && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-gray-500">Showing {filteredEntries.length} of {entries.length} entries</span>
            {selectedTeamNames.map(team => (
              <button
                key={team.id}
                onClick={() => {
                  const newFilters = new Set(teamFilters)
                  newFilters.delete(team.id)
                  setTeamFilters(newFilters)
                }}
                className="flex items-center gap-1.5 px-2 py-1 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded-full text-xs"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${team.is_eliminated ? 'bg-red-400' : 'bg-green-500'}`} />
                {team.name}
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            ))}
            {teamFilters.size > 1 && (
              <button
                onClick={() => setTeamFilters(new Set())}
                className="text-xs text-gray-500 hover:text-red-500"
              >
                Clear all
              </button>
            )}
          </div>
        )}
        
        <div className="flex items-center justify-between text-xs text-gray-400 font-medium uppercase tracking-wide">
          <span>Team</span>
          <div className="flex items-center gap-6">
            <span className="w-12 text-center">Points</span>
            <span className="w-12 text-center">Fouls</span>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {filteredEntries.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm py-12 text-center">
          <div className="text-gray-400 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
              <path d="M2 17l10 5 10-5"/>
              <path d="M2 12l10 5 10-5"/>
            </svg>
          </div>
          <p className="text-gray-500">
            {entries.length === 0 
              ? 'No entries in the champion contest yet' 
              : 'No entries match your search'}
          </p>
        </div>
      )}

      {/* Entries grouped by team */}
      {filteredEntries.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-100">
          {groupedEntries.map((group) => (
            <div key={group.team_id}>
              {/* Team Header Row */}
              <div className="flex items-center px-4 py-3 bg-gray-50/50">
                <div className="flex items-center gap-3 flex-1">
                  <span className={`w-2 h-2 rounded-full ${group.team_alive ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <span className="text-sm font-semibold text-[#1e3a5f] w-5">{group.team_seed}</span>
                  <span className="font-medium text-gray-900">{group.team_name}</span>
                  <span className="text-xs text-gray-400">{group.team_region} • {group.entries.length} {group.entries.length === 1 ? 'pick' : 'picks'}</span>
                </div>
              </div>
              
              {/* Entries under this team */}
              <div className="divide-y divide-gray-50">
                {group.entries.map((entry) => (
                  <div key={entry.id} className="flex items-center px-4 py-2.5 pl-10">
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      {entry.in_contest && (
                        <span className="text-[10px] font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded flex-shrink-0">$</span>
                      )}
                      <span className={`text-sm font-medium ${entry.in_contest ? 'text-gray-700' : 'text-gray-400'}`}>{entry.entry_name}</span>
                      <span className="text-xs text-gray-400">{entry.user_name}</span>
                    </div>
                    <div className="flex items-center gap-6">
                      <span className="text-sm text-gray-600 font-medium w-12 text-center">{entry.championship_total_points}</span>
                      <span className="text-sm text-gray-400 w-12 text-center">{entry.championship_total_fouls}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

